import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnChanges,
  OnInit,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import {
  trigger,
  style,
  transition,
  animate,
  AnimationEvent,
} from '@angular/animations';
import { SvgComponent } from '../svg-component/svg-component.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { Comment } from '../../interfaces';
import { MomentPipe } from '../../pipes';
import { TranslateModule } from '@ngx-translate/core';
import { BusinessClockService } from '../../services/business-clock.service';
import moment from 'moment';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SvgComponent,
    MomentPipe,
    TranslateModule,
    SpinnerComponent,
  ],
  animations: [
    trigger('fadeOut', [
      transition(':leave', [
        style({ opacity: 1 }),
        animate('250ms ease-out', style({ opacity: 0 })),
      ]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('250ms ease-in', style({ opacity: 1 })),
      ]),
    ]),
  ],
})
export class CommentsComponent implements OnChanges, OnInit, OnDestroy {
  @Input() comments?: Comment[] = [];
  @Input() commentControl: FormControl = new FormControl('');
  @Input() commentPending?: boolean;
  @Input() commentFailed?: boolean;
  @Input() newComment?: Comment;
  @Input() errorMessage?: string;
  @Output() onAddComment = new EventEmitter<string>();
  @Output() hideFailedCommentEvent = new EventEmitter<void>();
  fadeOutAnimationDone = false;
  @ViewChild('headerContainer') headerContainerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('newContentMeasure')
  newContentMeasureRef?: ElementRef<HTMLDivElement>;

  showHeaderContainer = false;
  showSpinner = false;
  showNewHeaderContent = false;
  constructor(private _businessClockService: BusinessClockService) {}

  ngOnInit(): void {
    this.syncCommentControlDisabled();
  }

  commentMoment(dateTimeComment: string): moment.Moment {
    return this._businessClockService.toBusinessMoment(dateTimeComment);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['commentPending'] || changes['commentControl']) {
      this.syncCommentControlDisabled();
    }
    if (changes['commentPending']) {
      const currentPending = changes['commentPending'].currentValue as
        | boolean
        | undefined;
      if (currentPending) {
        this.showHeaderContainer = true;
        this.showSpinner = true;
        this.showNewHeaderContent = false;
        this.fadeOutAnimationDone = false;
      } else if (currentPending === false && this.newComment) {
        this.startHeightTransitionThenSwap();
      }
    }
    if (
      changes['newComment'] &&
      this.commentPending === false &&
      changes['newComment'].currentValue
    ) {
      this.startHeightTransitionThenSwap();
    }
  }

  addComment() {
    if (this.commentControl.invalid) return;
    const commentText = this.commentControl.value?.trim();
    if (!commentText) return;
    this.onAddComment.emit(commentText);
    this.newComment = undefined;
  }

  onFadeOutStart() {
    this.fadeOutAnimationDone = false;
  }

  onFadeOutDone(event: AnimationEvent) {
    this.fadeOutAnimationDone = event.toState === 'void';
    if (this.fadeOutAnimationDone) {
      this.showNewHeaderContent = true;
      // allow the content to take natural height after swap
      const container = this.headerContainerRef?.nativeElement;
      if (container) {
        container.style.height = '';
        container.style.transition = '';
      }
    }
  }

  hideFailedComment() {
    this.hideFailedCommentEvent.emit();
  }

  private syncCommentControlDisabled(): void {
    const pending = this.commentPending ?? false;
    if (pending) {
      this.commentControl.disable({ emitEvent: false });
    } else {
      this.commentControl.enable({ emitEvent: false });
    }
  }

  private startHeightTransitionThenSwap() {
    const container = this.headerContainerRef?.nativeElement;
    const measure = this.newContentMeasureRef?.nativeElement;
    if (!container || !measure) {
      // Fallback: just proceed with spinner fade out
      this.showSpinner = false;
      this.fadeOutAnimationDone = true;
      return;
    }
    const currentHeight = container.clientHeight;
    const targetHeight = measure.scrollHeight;
    if (targetHeight <= currentHeight) {
      // No need to grow; proceed to fade spinner out
      this.showSpinner = false;
      this.fadeOutAnimationDone = true;
      return;
    }
    container.style.height = currentHeight + 'px';
    const heightDelta = Math.abs(targetHeight - currentHeight);
    const durationSeconds = heightDelta / 100; // 100px per second
    container.style.transition = `height ${durationSeconds}s ease`;
    // next frame to apply target height
    requestAnimationFrame(() => {
      container.style.height = targetHeight + 'px';
      const onEnd = (ev: TransitionEvent) => {
        if (ev.propertyName === 'height') {
          container.removeEventListener('transitionend', onEnd);
          // now trigger spinner fade out
          this.showSpinner = false;
          this.fadeOutAnimationDone = true;
        }
      };
      container.addEventListener('transitionend', onEnd);
    });
  }

  ngOnDestroy(): void {
    this.commentControl.reset();
  }
}
